import React from "react";
import dayjs, { Dayjs } from "dayjs";

import { Button, IconButton, MenuItem, Select, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

import Spinner from "../../Components/Spinner/Spinner";
import ErrorDialogue from "../../Components/ErrorDialogue/ErrorDialogue";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";

import CountPeriodRepository from "../../Repositories/CountPeriodRepository";
import CountPeriodResponse from "../../Repositories/CountPeriodResponses/CountPeriodResponse";
import { CountPeriodType } from "../../Types/Enums/CountPeriodType";

interface CountPeriodManagementPageStates {
    loadedCountPeriods: CountPeriodResponse[];
    isLoading: boolean;

    showError: boolean;
    errorTitle: string;
    errorMsg: string;

    startDate: Dayjs;
    endDate: Dayjs;
    transactionId: string;
    type: CountPeriodType;

    isAdding: boolean;
    isDeletingId: string | null;

    editingId: string | null;
    editLocationId: string;
    editTransactionId: string;
    editType: CountPeriodType;
    editStartDate: Dayjs;
    editEndDate: Dayjs;
    editIsDeleted: boolean;

    isSavingId: string | null;
}

export default class CountPeriodManagementPage extends React.Component<{}, CountPeriodManagementPageStates> {
    static contextType = AuthContext;
    context!: AuthContextType;

    constructor(props: any) {
        super(props);

        this.getCountPeriods = this.getCountPeriods.bind(this);
        this.addCountPeriod = this.addCountPeriod.bind(this);
        this.deleteCountPeriod = this.deleteCountPeriod.bind(this);
        this.startEdit = this.startEdit.bind(this);
        this.cancelEdit = this.cancelEdit.bind(this);
        this.saveEdit = this.saveEdit.bind(this);

        const now = dayjs(Date.now());

        this.state = {
            loadedCountPeriods: [],
            isLoading: false,

            showError: false,
            errorTitle: "",
            errorMsg: "",

            startDate: now.startOf("month"),
            endDate: now.endOf("month"),
            transactionId: "",
            type: CountPeriodType.Month,

            isAdding: false,
            isDeletingId: null,

            editingId: null,
            editLocationId: "",
            editTransactionId: "",
            editType: CountPeriodType.Month,
            editStartDate: now.startOf("month"),
            editEndDate: now.endOf("month"),
            editIsDeleted: false,

            isSavingId: null,
        };
    }

    public componentDidMount(): void {
        this.setState({ isLoading: true }, () => this.getCountPeriods());
    }

    private typeName(t: number) {
        switch (t) {
            case CountPeriodType.Day: return "Day";
            case CountPeriodType.Week: return "Week";
            case CountPeriodType.Month: return "Month";
            case CountPeriodType.Year: return "Year";
            default: return "Undefined";
        }
    }

    private normalizeGuidOrEmpty(value: string) {
        const v = (value ?? "").trim();
        return v.length ? v : "00000000-0000-0000-0000-000000000000";
    }

    private async getCountPeriods() {
        const result = await CountPeriodRepository.getAllCountPeriods(this.context.locationId);
        if (result.isFailed()) {
            this.setState({
                errorTitle: result.errorTitle ?? "",
                errorMsg: result.errorMessage ?? "",
                showError: true,
                isLoading: false,
            });
            return;
        }

        const countPeriods = result.result!.countPeriods
            .slice()
            .sort((a: any, b: any) => dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf());

        this.setState({
            loadedCountPeriods: countPeriods as CountPeriodResponse[],
            isLoading: false,
        });
    }

    private async addCountPeriod() {
        if (this.state.isAdding) return;

        const start = this.state.startDate.startOf("day");
        const end = this.state.endDate.startOf("day");

        if (end.isBefore(start)) {
            this.setState({
                showError: true,
                errorTitle: "Invalid period",
                errorMsg: "End date must be on or after start date.",
            });
            return;
        }

        this.setState({ isAdding: true });

        const tx = this.normalizeGuidOrEmpty(this.state.transactionId);

        const result = await CountPeriodRepository.addCountPeriod(
            this.context.locationId,
            tx,
            this.state.type as unknown as number,
            start,
            end
        );

        if (result.isFailed()) {
            this.setState({
                errorTitle: result.errorTitle ?? "",
                errorMsg: result.errorMessage ?? "",
                showError: true,
                isAdding: false,
            });
            return;
        }

        await this.getCountPeriods();

        this.setState({
            isAdding: false,
            transactionId: "",
        });
    }

    private async deleteCountPeriod(countPeriodId: string) {
        if (this.state.isDeletingId || this.state.isSavingId) return;

        this.setState({ isDeletingId: countPeriodId });

        const result = await CountPeriodRepository.deleteCountPeriod(this.context.locationId, countPeriodId);
        if (result.isFailed()) {
            this.setState({
                errorTitle: result.errorTitle ?? "",
                errorMsg: result.errorMessage ?? "",
                showError: true,
                isDeletingId: null,
            });
            return;
        }

        this.setState(prev => ({
            loadedCountPeriods: prev.loadedCountPeriods.filter(x => x.id !== countPeriodId),
            isDeletingId: null,
            editingId: prev.editingId === countPeriodId ? null : prev.editingId,
        }));
    }

    private startEdit(p: CountPeriodResponse) {
        this.setState({
            editingId: p.id,
            editLocationId: p.locationId,
            editTransactionId: this.normalizeGuidOrEmpty(p.transactionId ?? ""),
            editType: Number(p.type) as CountPeriodType,
            editStartDate: dayjs(p.startDate),
            editEndDate: dayjs(p.endDate),
            editIsDeleted: false,
        });
    }

    private cancelEdit() {
        this.setState({ editingId: null });
    }

    private async saveEdit(p: CountPeriodResponse) {
        if (this.state.isSavingId) return;

        const start = this.state.editStartDate.startOf("day");
        const end = this.state.editEndDate.startOf("day");

        if (end.isBefore(start)) {
            this.setState({
                showError: true,
                errorTitle: "Invalid period",
                errorMsg: "End date must be on or after start date.",
            });
            return;
        }

        this.setState({ isSavingId: p.id });

        const update = {
            locationId: this.state.editLocationId,
            transactionId: this.normalizeGuidOrEmpty(this.state.editTransactionId),
            type: this.state.editType as unknown as number,
            startDate: start,
            endDate: end,
            isDeleted: this.state.editIsDeleted,
        };

        const result = await CountPeriodRepository.updateCountPeriod(this.context.locationId, p.id, update);
        if (result.isFailed()) {
            this.setState({
                errorTitle: result.errorTitle ?? "",
                errorMsg: result.errorMessage ?? "",
                showError: true,
                isSavingId: null,
            });
            return;
        }

        await this.getCountPeriods();
        this.setState({ isSavingId: null, editingId: null });
    }

    private Paper(opts: { title: string; minHeight?: number; fill?: boolean; children?: React.ReactNode }) {
        const { title, minHeight = 180, fill = false, children } = opts;

        return (
            <div
                role="region"
                aria-label={title}
                style={{
                    width: "100%",
                    background: "#FFFFFF",
                    borderRadius: "16px",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.06)",
                    padding: "16px",
                    boxSizing: "border-box",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    minHeight: `${minHeight}px`,
                    flex: fill ? 1 : undefined,
                    overflow: "visible",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        flex: "0 0 auto",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontWeight: 700,
                            fontSize: "14px",
                            opacity: 0.9,
                            userSelect: "none",
                        }}
                    >
                        <span
                            style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                background: "#c79656",
                                display: "inline-block",
                            }}
                        />
                        {title}
                    </div>
                </div>

                <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "stretch", justifyContent: "stretch", overflow: "visible" }}>
                    <div style={{ width: "100%", height: "100%", display: "flex", minHeight: 0 }}>{children}</div>
                </div>
            </div>
        );
    }

    private SelectLikeTextField(props: { label: string; value: any; onChange: (v: any) => void; items: { value: any; label: string }[] }) {
        return (
            <TextField
                select
                label={props.label}
                value={props.value}
                onChange={(e) => props.onChange(e.target.value)}
                fullWidth
                SelectProps={{ displayEmpty: true }}
            >
                {props.items.map((x) => (
                    <MenuItem key={String(x.value)} value={x.value}>
                        {x.label}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    public render() {
        const accent = "#c79656";

        return (
            <div style={{ width: "100%", height: "100%", boxSizing: "border-box", padding: "28px", overflow: "visible", display: "flex", flexDirection: "column", gap: "16px" }}>
                {this.state.isLoading && <Spinner />}

                <div style={{ width: "100%", maxWidth: "1200px", margin: "0 auto", boxSizing: "border-box", display: "flex", alignItems: "center", gap: "12px", paddingLeft: "18px", paddingRight: "18px" }}>
                    <div style={{ width: "6px", height: "36px", background: accent, borderRadius: "6px" }} />
                    <div style={{ fontSize: "28px", fontWeight: 800, lineHeight: 1.1, color: "#1a1a1a", letterSpacing: "0.2px" }}>
                        Count periods
                    </div>
                </div>

                <div style={{ flex: 1, minHeight: 0, overflow: "visible", boxSizing: "border-box" }}>
                    <div style={{ width: "100%", maxWidth: "1200px", height: "100%", margin: "0 auto", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "16px", paddingLeft: "18px", paddingRight: "18px", overflow: "visible", minHeight: 0 }}>
                        {this.Paper({
                            title: "Add period",
                            minHeight: 210,
                            children: (
                                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", width: "100%", alignItems: "stretch" }}>
                                        <TextField
                                            label="Start date"
                                            type="date"
                                            value={this.state.startDate.format("YYYY-MM-DD")}
                                            onChange={(e) => this.setState({ startDate: dayjs(e.target.value) })}
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                        />
                                        <TextField
                                            label="End date"
                                            type="date"
                                            value={this.state.endDate.format("YYYY-MM-DD")}
                                            onChange={(e) => this.setState({ endDate: dayjs(e.target.value) })}
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                        />
                                        {this.SelectLikeTextField({
                                            label: "Type",
                                            value: this.state.type,
                                            onChange: (v) => this.setState({ type: v as CountPeriodType }),
                                            items: [
                                                { value: CountPeriodType.Day, label: "Day" },
                                                { value: CountPeriodType.Week, label: "Week" },
                                                { value: CountPeriodType.Month, label: "Month" },
                                                { value: CountPeriodType.Year, label: "Year" },
                                            ],
                                        })}
                                        <TextField label="TransactionId" value={this.state.transactionId} onChange={(e) => this.setState({ transactionId: e.target.value })} fullWidth />
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                        <Button
                                            variant="contained"
                                            onClick={this.addCountPeriod}
                                            disabled={this.state.isAdding}
                                            startIcon={<AddIcon />}
                                            style={{ background: accent, color: "#fff", borderRadius: 10, textTransform: "none", fontWeight: 700, paddingLeft: 14, paddingRight: 14 }}
                                        >
                                            Add
                                        </Button>
                                    </div>
                                </div>
                            ),
                        })}

                        {this.Paper({
                            title: `Overview (${this.state.loadedCountPeriods.length})`,
                            fill: true,
                            children: (
                                <div style={{ width: "100%", height: "100%", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                                    <div style={{ width: "100%", height: "100%", minHeight: 0, display: "flex", flexDirection: "column", gap: "10px", overflowY: "auto", overflowX: "hidden", padding: "2px 8px 10px 2px", boxSizing: "border-box" }}>
                                        {this.state.loadedCountPeriods.map((p) => {
                                            const start = dayjs(p.startDate).format("YYYY-MM-DD");
                                            const end = dayjs(p.endDate).format("YYYY-MM-DD");
                                            const tx = (p.transactionId ?? "").toString();
                                            const typeLabel = this.typeName(Number(p.type));
                                            const isEditing = this.state.editingId === p.id;
                                            const isBusy = this.state.isDeletingId === p.id || this.state.isSavingId === p.id;

                                            return (
                                                <div key={p.id} style={{ width: "100%", background: "#fff", borderRadius: "14px", border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 12px 34px rgba(0,0,0,0.05)", padding: "12px 12px", display: "flex", flexDirection: "column", gap: "10px", boxSizing: "border-box" }}>
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                                                        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                                                <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.6px", color: "rgba(26,26,26,0.70)" }}>
                                                                    {typeLabel}
                                                                </div>
                                                                <div style={{ height: 18, width: 1, background: "rgba(0,0,0,0.12)" }} />
                                                                <div style={{ fontSize: 14, fontWeight: 800, color: "#1a1a1a" }}>
                                                                    {start} → {end}
                                                                </div>
                                                            </div>

                                                            <div style={{ fontSize: 12, color: "rgba(26,26,26,0.65)", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                TransactionId: {tx && tx !== "00000000-0000-0000-0000-000000000000" ? tx : "—"}
                                                            </div>
                                                        </div>

                                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                            {!isEditing && (
                                                                <IconButton onClick={() => this.startEdit(p)} disabled={!!this.state.editingId || !!this.state.isDeletingId || !!this.state.isSavingId} style={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.10)", background: "rgba(0,0,0,0.04)" }}>
                                                                    <EditIcon style={{ color: "rgba(26,26,26,0.70)" }} />
                                                                </IconButton>
                                                            )}

                                                            <IconButton onClick={() => this.deleteCountPeriod(p.id)} disabled={isBusy || !!this.state.editingId} style={{ borderRadius: 12, border: "1px solid rgba(199,150,86,0.35)", background: "rgba(199,150,86,0.08)" }}>
                                                                <DeleteIcon style={{ color: accent }} />
                                                            </IconButton>
                                                        </div>
                                                    </div>

                                                    {isEditing && (
                                                        <div style={{ width: "100%", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", alignItems: "stretch" }}>
                                                            <TextField label="LocationId" value={this.state.editLocationId} onChange={(e) => this.setState({ editLocationId: e.target.value })} fullWidth />
                                                            <TextField label="TransactionId" value={this.state.editTransactionId} onChange={(e) => this.setState({ editTransactionId: e.target.value })} fullWidth />
                                                            {this.SelectLikeTextField({
                                                                label: "Type",
                                                                value: this.state.editType,
                                                                onChange: (v) => this.setState({ editType: v as CountPeriodType }),
                                                                items: [
                                                                    { value: CountPeriodType.Day, label: "Day" },
                                                                    { value: CountPeriodType.Week, label: "Week" },
                                                                    { value: CountPeriodType.Month, label: "Month" },
                                                                    { value: CountPeriodType.Year, label: "Year" },
                                                                ],
                                                            })}
                                                            <TextField
                                                                label="Start date"
                                                                type="date"
                                                                value={this.state.editStartDate.format("YYYY-MM-DD")}
                                                                onChange={(e) => this.setState({ editStartDate: dayjs(e.target.value) })}
                                                                InputLabelProps={{ shrink: true }}
                                                                fullWidth
                                                            />
                                                            <TextField
                                                                label="End date"
                                                                type="date"
                                                                value={this.state.editEndDate.format("YYYY-MM-DD")}
                                                                onChange={(e) => this.setState({ editEndDate: dayjs(e.target.value) })}
                                                                InputLabelProps={{ shrink: true }}
                                                                fullWidth
                                                            />

                                                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                                                                <Button variant="outlined" onClick={this.cancelEdit} startIcon={<CloseIcon />} disabled={this.state.isSavingId === p.id} style={{ borderRadius: 10, textTransform: "none", fontWeight: 700, paddingLeft: 14, paddingRight: 14, borderColor: "rgba(0,0,0,0.18)", color: "rgba(26,26,26,0.75)" }}>
                                                                    Cancel
                                                                </Button>

                                                                <Button variant="contained" onClick={() => this.saveEdit(p)} startIcon={<SaveIcon />} disabled={this.state.isSavingId === p.id} style={{ background: accent, color: "#fff", borderRadius: 10, textTransform: "none", fontWeight: 700, paddingLeft: 14, paddingRight: 14 }}>
                                                                    Save
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ),
                        })}
                    </div>
                </div>

                {this.state.showError && (
                    <ErrorDialogue
                        show={this.state.showError}
                        title={this.state.errorTitle}
                        message={this.state.errorMsg}
                        onClose={() => this.setState({ showError: false })}
                    />
                )}
            </div>
        );
    }
}
